import React, { useCallback, useState } from 'react';
import {
  Card, CardHeader, Box, Typography, Button, CardContent, CardActions,
} from '@mui/material';
import { Link } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import { useConfirm } from 'material-ui-confirm';
import { useApiClient, useFetch, OutlinedIcons } from '@marageti/z4-lib';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { enqueueSnackbar } from 'notistack';
import AddItineraryDialog from '../../../../components/zicasso-dialogs/add-itinerary';
import LogCallDialog from '../../../../components/zicasso-dialogs/log-call';
import { getDueStatus, ReminderMap } from '../../../../utils/reminders';
import { calculateRemainingTime, formatFirstMessageHoursLeft } from '../../../../utils/helpers';
import { useAuthStore } from '../../../../store';
import useMessageProcessedListener from '../../../../hooks/use-message-processed-listener';

const { OutlinedExclamationIcon } = OutlinedIcons;

const SALES_STAGES = [
  { key: 'FIRST_MESSAGE_SENT', label: 'First Message' },
  { key: 'CONSULTATION', label: 'Consultation' },
  { key: 'ITINERARY_SENT', label: 'Proposal Sent' },
  { key: 'ITINERARY_IN_REVIEW', label: 'Itinerary Review' },
];

const stageDetails: Record<string, { title: string; description: string; buttonType: string[] }> = {
  FIRST_MESSAGE_SENT: {
    title: 'Next: Consultation Call',
    description:
      "Make a phone call to discuss trip details and the traveler's preferences for their journey. Don't forget to log your call attempts here to track your progress.",
    buttonType: ['logCall', 'sendMessage'],
  },
  CONSULTATION: {
    title: 'Next: Send Trip Proposal',
    description:
      'Upload the first itinerary and keep the trip planning moving! Travelers tend to be more engaged once they receive a personalized itinerary.',
    buttonType: ['uploadItnerary', 'sendMessage'],
  },
  ITINERARY_SENT: {
    title: 'Next: Review Proposal With Traveler',
    description:
      "Follow up with the traveler about the itinerary proposal you sent. Don't forget to log your call attempts here to track your progress.",
    buttonType: ['sendMessage', 'logCall'],
  },
  ITINERARY_IN_REVIEW: {
    title: "You're Almost There!",
    description:
      "Keep the traveler engaged during the itinerary review process. Don't forget to log your call attempts here to track your progress.",
    buttonType: ['sendMessage', 'logCall'],
  },
};

const firstMessageDetails = {
  title: 'Send First Message',
  description: 'Send a message to the traveler with a brief intro about your company, itinerary ideas, and an invitation to schedule a call.',
  buttonType: ['sendMessage'],
};

const inactiveLeads: Record<string, { index: number }> = {
  FIRST_MESSAGE_SENT: {
    index: 9,
  },
  CONSULTATION: {
    index: 9,
  },
  ITINERARY_SENT: {
    index: 9,
  },
  ITINERARY_IN_REVIEW: {
    index: 7,
  },
};

const tripNeedsFirstMessage = (tripMatch: TripMatch | undefined, agent: Agent | null) => (
  tripMatch?.status === TripMatchStatus.MatchAccepted && agent && agent.userInfo.some((userInfo) => !userInfo.roles?.includes(RoleName.AGENT_SUPPORT))
);

const SalesStageCard = ({
  salesStage,
  reminder,
  travelerToPartnerId,
  tripMatchId,
  tripMatchCreatedAt,
}: {
  salesStage: string,
  reminder: { dueAt: string, index: number, startAt: string, origDueAt: string },
  travelerToPartnerId: string,
  tripMatchId: string,
  tripMatchCreatedAt: string,
}) => {
  const confirm = useConfirm();
  const apiClient = useApiClient();
  const { loggedInAgent } = useAuthStore();
  const { data: tripMatch, mutate } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], true);

  // Sales stage / reminder are updated asynchronously by the worker after a phone call (or any
  // logged message) is created. The worker fires MESSAGE_PROCESSED on the conversation channel
  // when those side effects are applied, so refetch tripMatch then to pick up the new stage /
  // reminder. Without this, the card stays stale until the user navigates away and back.
  const onMessageProcessed = useCallback(() => {
    mutate();
  }, [mutate]);
  useMessageProcessedListener(travelerToPartnerId, onMessageProcessed);

  const [openItineraryDialog, setOpenItineraryDialog] = useState(false);
  const [openLogCallDialog, setOpenLogCallDialog] = useState(false);

  const { isDue: isReminderDue, isOverdue: isReminderOverdue } = getDueStatus(reminder);
  // if salesStage is not in stageDetails assume it's waiting on the first message sent
  const isFirstMessageReminder = tripNeedsFirstMessage(tripMatch, loggedInAgent);
  let isFirstMessageOverdue = false;
  // if salesStage is not in stageDetails use firstMessageDetails
  const details = stageDetails[salesStage] || firstMessageDetails;
  const description = (stageDetails[salesStage] && (isReminderDue || isReminderOverdue)) ? ReminderMap[salesStage][reminder.index]?.longDescription : details.description;
  const { title } = details;
  let titleComponent:React.ReactNode = null;

  if (!stageDetails[salesStage] && !isFirstMessageReminder) {
    return null;
  }

  if (stageDetails[salesStage] && (isReminderDue || isReminderOverdue)) {
    const titlePrefix = (isReminderDue && !isReminderOverdue) ? 'Reminder: ' : 'Overdue: ';
    const titleString = `${titlePrefix}${ReminderMap[salesStage][reminder.index]?.title || details.title}`;
    titleComponent = (
      <Box display="flex" alignItems="center" gap={1}>
        {isReminderOverdue && <OutlinedExclamationIcon sx={{ color: 'warning.main', fontSize: 24 }} />}
        {titleString}
      </Box>
    );
  }
  if (isFirstMessageReminder) {
    const remainingTime = calculateRemainingTime(tripMatchCreatedAt);
    isFirstMessageOverdue = remainingTime === 0;
    const titlePrefix = isFirstMessageOverdue ? 'Overdue: ' : 'Reminder: ';
    const titleString = `${titlePrefix}Send First Message${isFirstMessageOverdue ? '' : ` (${formatFirstMessageHoursLeft(remainingTime)})`}`;
    titleComponent = (
      <Box display="flex" alignItems="center" gap={1}>
        {isFirstMessageOverdue && <OutlinedExclamationIcon sx={{ color: 'warning.main', fontSize: 24 }} />}
        {titleString}
      </Box>
    );
  }

  const onClickInactive = () => {
    confirm({
      title: 'Mark Trip as Inactive?',
      description: (
        <>
          Inactive trips can be reactivated in the future and remain visible to travelers, who can still send messages.
          Trips should only be marked as inactive after you have completed the follow-up timeline or the travel dates have passed.
        </>
      ),
      confirmationText: 'Confirm',
    })
      .then(async () => {
        if (tripMatchId) {
          try {
            await apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId, {
              id: tripMatchId,
              status: TripMatchStatus.MatchInactive,
              statusInfo: {
                reason: 'Trip marked as inactive by Agent',
              },
            });
            mutate();
            enqueueSnackbar('Trip marked as inactive.', { variant: 'success' });
          } catch (error) {
            console.error(error);
          }
        }
      })
      .catch(() => {});
  };

  const buttonTypes: Record<string, { label: string, to?: string, onClick?: () => void }> = {
    sendMessage: {
      label: 'Send Message',
      to: `../messages/${travelerToPartnerId}`,
    },
    uploadItnerary: {
      label: 'Upload Itinerary',
      onClick: () => {
        setOpenItineraryDialog(true);
      },
    },
    logCall: {
      label: 'Log Phone Call',
      onClick: () => {
        setOpenLogCallDialog(true);
      },
    },
    markInactive: {
      label: 'Mark as Inactive',
      onClick: () => {
        onClickInactive();
      },
    },
  };

  const isInactiveLead = inactiveLeads[salesStage] && reminder.index === inactiveLeads[salesStage].index;
  if (isInactiveLead) {
    details.buttonType = ['markInactive', 'sendMessage'];
  }

  const ActionButtons: React.ReactNode[] = details.buttonType.map((type) => {
    if (buttonTypes[type].to) {
      return (
        // @ts-ignore
        <Button key={type} variant="outlined" color="primary" sx={{ mr: 2, ml: '0 !important' }} component={Link} to={buttonTypes[type].to}>
          {buttonTypes[type].label}
        </Button>
      );
    }
    return (
      <Button key={type} variant="outlined" color="primary" sx={{ mr: 2, ml: '0 !important' }} onClick={buttonTypes[type].onClick}>
        {buttonTypes[type].label}
      </Button>
    );
  });

  const completedIndex = SALES_STAGES.findIndex((s) => s.key === salesStage);
  const SalesStageStepper: React.ReactNode = (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        {SALES_STAGES.map((stage, idx) => {
          const isCompleted = idx <= completedIndex;
          let circleBg = '#e0e0e0';
          let circleBorder = '3px solid #e0e0e0';
          let circleColor = '#bdbdbd';
          if (isCompleted) {
            circleBg = '#008147';
            circleBorder = 'none';
            circleColor = '#fff';
          }
          let connector = null;
          if (idx < completedIndex) {
            connector = (
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  background: '#008147',
                  mx: 0.3,
                  borderRadius: 2,
                }}
              />
            );
          } else if (idx === completedIndex) {
            connector = (
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  mx: 0.3,
                  borderTopRightRadius: 12,
                  borderBottomRightRadius: 12,
                  borderTopLeftRadius: 12,
                  borderBottomLeftRadius: 12,
                  background: 'linear-gradient(to right, #008147 0%, #008147 50%, #d5d5d5 50%, #d5d5d5 100%)',
                }}
              />
            );
          } else {
            connector = (
              <Box
                sx={{
                  flex: 1,
                  height: 6,
                  background: '#d5d5d5',
                  mx: 0.3,
                  borderRadius: 2,
                }}
              />
            );
          }
          return (
            <Box key={stage.key} display="flex" alignItems="center" flex={1}>
              <Box
                sx={{
                  width: isCompleted ? 24 : 16,
                  height: isCompleted ? 24 : 16,
                  borderRadius: '50%',
                  background: circleBg,
                  border: circleBorder,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  color: circleColor,
                  fontWeight: 700,
                  fontSize: 20,
                }}
              >
                {isCompleted ? (
                  <CheckIcon sx={{ color: '#fff', fontSize: 18 }} />
                ) : (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: 'transparent',
                    }}
                  />
                )}
              </Box>
              {connector}
            </Box>
          );
        })}
      </Box>
      <Box display="flex" justifyContent="space-between" mb={2}>
        {SALES_STAGES.map((stage, idx) => {
          const isCompleted = idx <= completedIndex;
          return (
            <Typography
              key={stage.key}
              variant="body"
              sx={{
                flex: 1,
                textAlign: 'left',
                fontWeight: 700,
                color: isCompleted ? 'text.primary' : 'text.secondary',
              }}
            >
              {stage.label}
            </Typography>
          );
        })}
      </Box>
    </>
  );
  // eslint-disable-next-line no-nested-ternary
  let backgroundColor = isReminderDue ? 'info.light' : isReminderOverdue ? 'warning.light' : 'background.paper';
  if (isFirstMessageReminder) {
    backgroundColor = isFirstMessageOverdue ? 'warning.light' : 'info.light';
  }
  return (
    <Card
      sx={{
        width: '100%',
        // eslint-disable-next-line no-nested-ternary
        backgroundColor,
      }}
    >
      <CardHeader titleTypographyProps={{ variant: 'h3', mb: 1 }} title="Sales Stage" />
      <CardContent>
        {SalesStageStepper}
        <Box mt={4}>
          <Typography variant="subtitle1" fontWeight={700} mb="4px">
            {titleComponent || title}
          </Typography>
          <Typography variant="body" mb={1} color="text.secondary">
            {description}
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ pt: 2 }}>
        {ActionButtons}
      </CardActions>
      <AddItineraryDialog
        closeDialog={() => setOpenItineraryDialog(false)}
        onDocumentUploaded={() => { mutate(); }}
        open={openItineraryDialog}
        tripMatchId={tripMatchId}
      />
      <LogCallDialog
        closeDialog={() => setOpenLogCallDialog(false)}
        onSuccess={() => { mutate(); }}
        open={openLogCallDialog}
        conversationId={travelerToPartnerId}
      />
    </Card>
  );
};

export default SalesStageCard;
